import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useBetting } from '@/Context/BettingContext';
import { useSportsMainMarkets } from '@/hooks/useSportsMainData';
import styles from './Sports.module.css';

const getOddsPrice = (val) => {
  if (val == null) return '--';
  if (typeof val === 'object' && val.price != null) return val.price;
  if (typeof val === 'number') return val;
  return val || '--';
};

const isFootball = (match) =>
  match.sportId === 1 ||
  match.sportId === '1' ||
  match.sportName?.toLowerCase().includes('soccer') ||
  match.sportName?.toLowerCase().includes('football');

const Sports = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const { addBet } = useBetting();

  const { data: cricketMatches = [] } = useSportsMainMarkets(4, 'in-play');
  const { data: footballMatches = [] } = useSportsMainMarkets(1, 'in-play');

  const liveMatches = [...cricketMatches, ...footballMatches]
    .sort((a, b) => {
      const aIPL = (a.competitionName || '').toLowerCase().includes('indian premier league');
      const bIPL = (b.competitionName || '').toLowerCase().includes('indian premier league');
      if (aIPL && !bIPL) return -1;
      if (!aIPL && bIPL) return 1;
      const aCricket = a.sportId === 4 || a.sportId === '4';
      const bCricket = b.sportId === 4 || b.sportId === '4';
      if (aCricket && !bCricket) return -1;
      if (!aCricket && bCricket) return 1;
      if (a.isLive && !b.isLive) return -1;
      if (!a.isLive && b.isLive) return 1;
      return 0;
    })
    .slice(0, 8);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'right' ? 320 : -320,
        behavior: 'smooth',
      });
    }
  };

  const handleCardClick = (match) => {
    navigate(`/sports/markets/${match.id || match.matchId}`, {
      state: {
        matchData: {
          id: match.id || match.matchId,
          matchId: match.id || match.matchId,
          gmid: match.gmid,
          sid: match.sid,
          eventid: match.eventid || match.gmid,
          eventId: match.eventid || match.gmid,
          team1: match.team1,
          team2: match.team2,
          matchTitle: match.matchTitle || `${match.team1} v ${match.team2}`,
          status: match.status,
          isLive: match.isLive,
          isSuspended: match.isSuspended || false,
          odds: match.odds,
          sportName: match.sportName,
          sportId: match.sportId,
          hasTV: match.hasTV || false,
        },
      },
    });
  };

  const handleOddsClick = (e, match, selectionName, odds) => {
    e.stopPropagation();
    const price = getOddsPrice(odds?.back);
    if (price === '--' || price == null) return;
    addBet({
      matchId: match.id || match.matchId,
      matchTitle: match.matchTitle || `${match.team1} v ${match.team2}`,
      selectionName,
      type: 'back',
      odds: price,
      market: 'MO',
      eventid: match.eventid,
      marketName: t('sports.matchOdds'),
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('home.liveSports')}</h2>
        <div className={styles.actions}>
          <button className={styles.viewAllBtn} onClick={() => navigate('/sports')}>
            {t('home.showMore')}
          </button>
          <div className={styles.scrollButtons}>
            <button className={styles.scrollButton} onClick={() => handleScroll('left')} aria-label="Scroll left">
              <ChevronLeft size={16} />
            </button>
            <button className={styles.scrollButton} onClick={() => handleScroll('right')} aria-label="Scroll right">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className={styles.scrollWrapper}>
        <div className={styles.scrollContainer} ref={scrollContainerRef}>
          {liveMatches.length === 0 && (
            <div style={{ padding: '20px', color: 'var(--color-text-secondary)', fontSize: '14px' }}>
              {t('home.noLiveMatches')}
            </div>
          )}
          {liveMatches.map((match) => {
            const team1Name = match.team1 || match.runners?.[0]?.runnerName || 'Team 1';
            const team2Name =
              match.team2 ||
              (match.runners?.length === 3
                ? match.runners[2]?.runnerName
                : match.runners?.[1]?.runnerName) ||
              'Team 2';
            const isSoccer = isFootball(match);
            const sportIcon = isSoccer ? '/assets/images/sports-icons/soccer.svg' : '/assets/images/sports-icons/cricket.svg';
            const sportLabel = isSoccer ? t('sports.football') : t('sports.cricket');
            const hasThreeRunners = match.runners?.length === 3 || match.team3;

            return (
              <div
                key={match.id}
                className={styles.matchCard}
                onClick={() => handleCardClick(match)}
              >
                {/* Card Header */}
                <div className={styles.cardHeader}>
                  <div className={styles.cardHeaderLeft}>
                    <img src={sportIcon} alt={sportLabel} className={styles.sportIcon} />
                    <span className={styles.sportLabel}>{sportLabel}</span>
                    <span className={styles.leagueSep}>·</span>
                    <span className={styles.leagueName}>
                      {match.competitionName || match.sportName}
                    </span>
                  </div>
                  <div className={styles.cardHeaderRight}>
                    {match.hasTV && (
                      <img
                        src="/assets/images/gift-card-icons/live-tv.svg"
                        alt="TV"
                        className={styles.tvIcon}
                      />
                    )}
                    {match.isLive ? (
                      <span className={styles.liveBadge}>
                        <span className={styles.liveDot} />
                        {t('sports.live').toUpperCase()}
                      </span>
                    ) : (
                      <span className={styles.timeBadge}>{match.status}</span>
                    )}
                  </div>
                </div>

                {/* Team Rows */}
                <div className={styles.teamsBlock}>
                  <div className={styles.teamRow}>
                    <span className={styles.teamName}>{team1Name}</span>
                    {match.isLive && (
                      <span className={styles.teamScore}>-</span>
                    )}
                  </div>
                  <div className={styles.teamRow}>
                    <span className={styles.teamName}>{team2Name}</span>
                    {match.isLive && (
                      <span className={styles.teamScore}>-</span>
                    )}
                  </div>
                </div>

                {/* Odds Row: 1 / X / 2 */}
                <div className={styles.oddsRow}>
                  <button
                    className={`${styles.oddsBtn} ${styles.oddsBtnBack}`}
                    onClick={(e) => handleOddsClick(e, match, team1Name, match.odds?.one)}
                  >
                    <span className={styles.oddsLabel}>1</span>
                    <span className={styles.oddsValue}>
                      {getOddsPrice(match.odds?.one?.back)}
                    </span>
                  </button>

                  {hasThreeRunners && (
                    <button
                      className={`${styles.oddsBtn} ${styles.oddsBtnDraw}`}
                      onClick={(e) =>
                        handleOddsClick(
                          e,
                          match,
                          match.team3 || match.runners?.[1]?.runnerName || 'The Draw',
                          match.odds?.x,
                        )
                      }
                    >
                      <span className={styles.oddsLabel}>X</span>
                      <span className={styles.oddsValue}>
                        {getOddsPrice(match.odds?.x?.back)}
                      </span>
                    </button>
                  )}

                  <button
                    className={`${styles.oddsBtn} ${styles.oddsBtnLay}`}
                    onClick={(e) => handleOddsClick(e, match, team2Name, match.odds?.two)}
                  >
                    <span className={styles.oddsLabel}>2</span>
                    <span className={styles.oddsValue}>
                      {getOddsPrice(match.odds?.two?.back)}
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Sports;
